import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, InputAdornment, LinearProgress, Stack, TextField, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { confirmRegisterSwitch, installRegisterSession } from '../utils/registerSession';

const apiUrl = path => `${process.env.REACT_APP_API_BASE_URL || ''}${path}`;
const labelFor = register => register.display_name || register.database_name;
const titleCase = value => value ? value[0].toUpperCase() + value.slice(1) : 'Access unavailable';
const unavailableLabel = reason => ({
  DATABASE_EXPIRED: 'Subscription expired', NO_EXPIRY_DATE: 'Subscription needs administrator attention',
  REGISTER_UNAVAILABLE: 'Register unavailable', ACCESS_REVOKED: 'Access revoked',
}[reason] || reason || 'Register unavailable');

export default function RegisterPicker({ open, onClose }) {
  const navigate = useNavigate();
  const [registers, setRegisters] = useState([]);
  const [administrator, setAdministrator] = useState(false);
  const [requiresSessionRefresh, setRequiresSessionRefresh] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [switching, setSwitching] = useState(null);
  const requestId = useRef(0);
  const mutationInFlight = useRef(false);
  const loadedToken = useRef(null);

  const loadRegisters = useCallback(async () => {
    const request = ++requestId.current;
    const token = localStorage.getItem('token');
    setLoading(true);
    setError('');
    setRegisters([]);
    setAdministrator(false);
    setRequiresSessionRefresh(false);
    loadedToken.current = null;
    try {
      if (!token) throw new Error('Please sign in to view your registers.');
      const response = await axios.get(apiUrl('/api/register-access'), { headers: { Authorization: `Bearer ${token}` } });
      if (request !== requestId.current || token !== localStorage.getItem('token')) return;
      if (!Array.isArray(response.data?.registers)) throw new Error('The register list could not be read. Please try again.');
      setRegisters(response.data.registers);
      setAdministrator(response.data.is_platform_admin === true);
      setRequiresSessionRefresh(response.data.requires_session_refresh === true);
      loadedToken.current = token;
    } catch (err) {
      if (request === requestId.current && token === localStorage.getItem('token')) {
        setError(err.response?.data?.message || err.message || 'Could not load your registers. Please try again.');
      }
    } finally {
      if (request === requestId.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) { setQuery(''); setSwitching(null); loadRegisters(); }
    return () => { requestId.current += 1; };
  }, [open, loadRegisters]);

  const switchRegister = async register => {
    if (mutationInFlight.current || (register.is_current && !requiresSessionRefresh) || !register.available) return;
    const token = loadedToken.current;
    if (!token || token !== localStorage.getItem('token')) {
      setError('Your session changed. Refresh the register list before continuing.');
      return;
    }
    mutationInFlight.current = true;
    try {
      if (!(await confirmRegisterSwitch())) return;
      if (token !== localStorage.getItem('token')) {
        setError('Your session changed. Refresh the register list before continuing.');
        return;
      }
      setSwitching(register.database_name);
      setError('');
      const response = await axios.post(apiUrl('/api/register-access/switch'), { database_name: register.database_name }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (token !== localStorage.getItem('token')) return;
      if (!response.data?.token) throw new Error('The register changed, but the replacement session was not returned. Please sign in again.');
      installRegisterSession(response.data.token);
      navigate('/dashboard', { replace: true });
    } catch (err) {
      if (token === localStorage.getItem('token')) setError(err.response?.data?.message || err.message || 'Could not change register. Please try again.');
    } finally {
      mutationInFlight.current = false;
      setSwitching(null);
    }
  };
  const visible = registers.filter(register => [labelFor(register), register.database_name, register.role]
    .some(value => String(value || '').toLowerCase().includes(query.trim().toLowerCase())))
    .sort((a, b) => Number(b.is_current) - Number(a.is_current) || labelFor(a).localeCompare(labelFor(b)));

  return <Dialog open={open} onClose={() => { if (!mutationInFlight.current) onClose(); }} fullWidth maxWidth="sm" aria-labelledby="register-picker-title">
    <DialogTitle id="register-picker-title">Change Register</DialogTitle>
    <DialogContent>
      <Stack spacing={2}>
        <Typography variant="body2" color="text.secondary">Choose a register assigned to your account. Your role and allocated seat are kept in each register when you switch.</Typography>
        <Typography variant="body2" color="text.secondary">This changes the active register for your account on other devices too. Any unfinished Android work stays with its original register.</Typography>
        {administrator && <Alert severity="info">Platform administrators can open all registers without using a seat.</Alert>}
        <TextField autoFocus fullWidth size="small" label="Search registers" value={query} onChange={event => setQuery(event.target.value)} disabled={Boolean(switching)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
        {loading && <LinearProgress aria-label="Loading registers" />}
        {error && <Alert severity="error" action={<Button color="inherit" size="small" disabled={Boolean(switching) || loading} onClick={loadRegisters}>Refresh</Button>}>{error}</Alert>}
        {!loading && !error && registers.length === 0 && <Alert severity="info">No registers are available for your account. Contact your administrator to request access.</Alert>}
        {!loading && registers.length > 0 && visible.length === 0 && <Typography color="text.secondary">No registers match your search.</Typography>}
        <Stack component="ul" spacing={1} sx={{ p: 0, m: 0, listStyle: 'none' }}>
          {visible.map(register => <Box component="li" key={register.database_name} sx={{ p: 1.5, border: '1px solid', borderColor: register.is_current ? 'primary.main' : 'divider', borderRadius: 1.5 }}>
            <Stack direction="row" alignItems="center" gap={2}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography fontWeight={600} sx={{ overflowWrap: 'anywhere' }}>{labelFor(register)}</Typography>
                <Stack direction="row" gap={0.75} flexWrap="wrap" sx={{ mt: 0.75 }}>
                  <Chip size="small" label={titleCase(register.role)} />
                  {register.is_db_supervisor && <Chip size="small" label="Supervisor" variant="outlined" />}
                  {register.is_current && <Chip size="small" label="Current register" color="primary" variant="outlined" />}
                </Stack>
                {!register.available && <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{unavailableLabel(register.unavailable_reason)}</Typography>}
              </Box>
              <Button variant={register.is_current ? 'text' : 'outlined'} size="small" disabled={loading || Boolean(switching) || (register.is_current && !requiresSessionRefresh) || !register.available}
                aria-label={`Open ${labelFor(register)}`} onClick={() => switchRegister(register)}>
                {switching === register.database_name ? 'Opening…' : register.is_current ? requiresSessionRefresh ? 'Reopen' : 'Current' : 'Open'}
              </Button>
            </Stack>
          </Box>)}
        </Stack>
      </Stack>
    </DialogContent>
    <DialogActions sx={{ px: 3, pb: 2 }}><Button disabled={Boolean(switching)} onClick={() => { if (!mutationInFlight.current) onClose(); }}>Close</Button></DialogActions>
  </Dialog>;
}
